import { useState, useCallback, useEffect, useRef } from "react";
import { Phone, X } from "lucide-react";
import { useSonaxWidget } from "@/hooks/integrations/useSonaxWidget";
import { getOuterContainer, getInnerContainer, expandWidget, collapseWidget, armSonax, disarmSonax } from "@/utils/sonaxWidgetControl";

export function SonaxHeaderButton() {
  const [isOpen, setIsOpen] = useState(false);
  const positioningRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { hasScript, isLoaded, openWidgetOnly } = useSonaxWidget();

  // Na montagem, esconde o #icon nativo e colapsa o widget (se nao estiver aberto)
  useEffect(() => {
    const timeout = setTimeout(() => {
      const inner = getInnerContainer();
      if (inner) {
        const icon = inner.querySelector('#icon') as HTMLElement;
        if (icon) icon.style.setProperty('display', 'none', 'important');
      }
      // So esconde o widget se ninguem o abriu ainda
      if ((window as any).__sonaxWidgetOpen !== true) {
        collapseWidget();
      }
    }, 2000);
    return () => clearTimeout(timeout);
  }, []);

  // Sincroniza com openWidgetAndCall (via PhoneCallButton ou outros)
  useEffect(() => {
    const interval = setInterval(() => {
      if ((window as any).__sonaxWidgetOpen === true && !isOpen) {
        setIsOpen(true);
        expandWidget();
        // NAO reseta para undefined — manter true para que Context nao interfira
      }
    }, 500);
    return () => clearInterval(interval);
  }, [isOpen]);

  // Mantém posicionamento enquanto aberto
  useEffect(() => {
    if (isOpen) {
      positioningRef.current = setInterval(() => expandWidget(), 2000);
    } else {
      if (positioningRef.current) {
        clearInterval(positioningRef.current);
        positioningRef.current = null;
      }
    }
    return () => {
      if (positioningRef.current) clearInterval(positioningRef.current);
    };
  }, [isOpen]);

  const handleClick = useCallback(() => {
    if (!isOpen) {
      // ABRIR — arma o gate (carrega/registra o ramal). Ação explícita do usuário.
      armSonax();
      const outer = getOuterContainer();
      if (outer) {
        // Widget existe no DOM — abre diretamente
        expandWidget();
        setTimeout(() => expandWidget(), 200);
        (window as any).__sonaxWidgetOpen = true;
      } else if (hasScript) {
        // Widget NÃO existe no DOM — carrega script e abre (sem discar/criar atividade)
        (window as any).__sonaxWidgetOpen = true;
        openWidgetOnly().catch(() => {
          (window as any).__sonaxWidgetOpen = false;
          setIsOpen(false);
        });
      } else {
        console.warn('⚠️ Sonax: script não configurado');
        return;
      }
      setIsOpen(true);
    } else {
      // FECHAR — desarma o gate (descarrega o script → ramal de-registra no SIP).
      collapseWidget();
      (window as any).__sonaxWidgetOpen = false;
      disarmSonax();
      setIsOpen(false);
    }
  }, [isOpen, hasScript, openWidgetOnly]);

  // Não mostra o botão se o script não está configurado
  if (!hasScript) return null;

  return (
    <button
      className="sonax-header-btn h-9 w-9 p-0 relative rounded-md text-white flex items-center justify-center cursor-pointer border-none outline-none"
      data-open={isOpen ? "true" : "false"}
      style={{ zIndex: 100000, position: 'relative' }}
      onClick={handleClick}
      title={isOpen ? "Fechar Softphone" : "Abrir Softphone"}
    >
      {isOpen ? <X className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
    </button>
  );
}
